import { useGetPermissions } from 'api-hooks/auth/query';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import {
  NavigatorParams,
  NavigatorParamsType,
} from 'common/constants/navigator-params';
import routes from 'common/constants/routes';
import { Content, LayoutContainer, ScrollArea } from 'common/layouts/styles';
import { themedStyled } from 'common/styles/styletron.theme';
import ScaffoldViewComponent from 'components/elements/scaffold-view-component';
import { errorStatusContent } from 'components/elements/scaffold-view-component/constant';
import { EditableTableProvider } from 'components/elements/table/use-editable-table';
import { BackButton } from 'components/elements/unauthorized-view-component';
import Header from 'components/widgets/header';
import SideNavigation, {
  SideNavigationRef,
} from 'components/widgets/side-navigation/side-navigation';
import { DialogProvider } from 'hooks/use-dialog';
import { DrawerProvider } from 'hooks/use-drawer';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import { useRouter } from 'next/router';
import * as React from 'react';

interface DefaultLayoutProps {
  children: React.ReactNode;
}

const allRoutes = routes
  .map((route) => {
    if ('children' in route) return route.children;
    return route;
  })
  .flat()
  .filter((route) => {
    return 'routeName' in route && 'component' in route;
  });

export default function DefaultLayout(props: DefaultLayoutProps) {
  //solve hydration problem (let all component render on client side)
  const [isMounted, setIsMounted] = React.useState(false);
  const [pageProps, setPageProps] = React.useState<any>(undefined);
  const sideNavigationRef = React.useRef<SideNavigationRef>(null);
  // const [isShowComponent, setIsShowComponent] = React.useState(false);

  const { children } = props;
  const { pathname, prefetch, isReady, query } = useRouter();
  const permissionQuery = useGetPermissions();

  const permissions = React.useMemo(() => {
    return permissionQuery?.data?.data || [];
  }, [permissionQuery?.data?.data]);

  const route = React.useMemo(() => {
    return allRoutes.find((route) => {
      return route.routeName === pathname;
    });
  }, [pathname]);

  const isPermitted = React.useMemo(() => {
    if (typeof route?.permission === 'undefined') return false; //
    if (route?.permission === AuthorizationRules.NoAuthorization) return true;
    return permissions.includes(route?.permission);
  }, [permissions, route?.permission]);

  const Component = route?.component;

  React.useEffect(() => {
    if (isMounted) return;
    setIsMounted(true);
    Promise.all(
      Object.keys(NavigationRoutes).map((key) =>
        prefetch(NavigationRoutes[key]),
      ),
    );
  }, [isMounted, prefetch]);

  React.useEffect(() => {
    if (!isReady) return;
    if (typeof window === 'undefined') return;
    // setIsShowComponent(false);
    const params = NavigatorParams.get(
      pathname as NavigationRoutes,
      query?.id as string | undefined,
    ) as NavigatorParamsType | null;
    setPageProps({
      ...query,
      ...params,
    });
    // setTimeout(() => {
    //   setIsShowComponent(true);
    // }, 10);
  }, [isReady, pathname, query]);

  if (pathname.includes('test')) return <>{children}</>;
  if (isMounted === false) return <></>;
  if (pathname === '/login') return <>{children}</>;

  return (
    <>
      <EditableTableProvider>
        <DialogProvider>
          <DrawerProvider>
            <LayoutContainer>
              <Header sideNavigationRef={sideNavigationRef} />
              <MobileWrapper>
                <SideNavigation ref={sideNavigationRef} />
                <Content>
                  <ScrollArea>
                    <>
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'fixed',
                          zIndex: 99,
                          pointerEvents: 'none',
                        }}
                      />
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          height: '100%',
                          padding: 16,
                          overflowY: 'auto',
                        }}
                      >
                        <ErrorBoundary
                          errorComponent={({ reset }) => {
                            return (
                              <ScaffoldViewComponent
                                title={errorStatusContent.default.title}
                                subtitle={errorStatusContent.default.subtitle}
                                message={errorStatusContent.default.message}
                                onRetry={reset}
                              />
                            );
                          }}
                        >
                          {isPermitted && Component && (
                            <Component
                              key={+new Date()}
                              id={query.id}
                              navigation={{ ...pageProps }}
                            />
                          )}
                          {!!route && !!permissions.length && !isPermitted && (
                            <ScaffoldViewComponent
                              title={errorStatusContent[403].title}
                              subtitle={errorStatusContent[403].subtitle}
                              message={errorStatusContent[403].message}
                              additionalContent={<BackButton />}
                            />
                          )}
                        </ErrorBoundary>
                      </div>
                    </>
                  </ScrollArea>
                </Content>
              </MobileWrapper>
            </LayoutContainer>
          </DrawerProvider>
        </DialogProvider>
      </EditableTableProvider>
    </>
  );
}

const MobileWrapper = themedStyled('div', {
  display: 'flex',
  flex: 1,
  height: 'calc(100vh - 64px)',
});
